import React from "react";

import cx from "classnames";

import { setStateValue } from "@kazoohr/helpers";

import { useIsMounted } from "../../helpers/useIsMounted";
import { Flex } from "../Flex";
import { Icon } from "../Icon";
import { Text } from "../Text";

import styles from "./Toggle.scss";

export interface ToggleProps {
  /**
   * Whether the toggle is off. If true, the toggle will be rendered in an inoperable state, but with `on/off` statuses.
   */
  inert?: boolean;
  /**
   * Label for the toggle. Not rendered, used for accessibility.
   */
  label?: string;
  /**
   * ID for the toggle, for associating labels to form elements (accessibility).
   */
  id?: string;
  /**
   * Unique test ID for the toggle.
   */
  "data-testid"?: string;
  /**
   * When toggled on, show this icon or React element.
   */
  statusOn?: JSX.Element | string;
  /**
   * When toggled off, show this icon or React element.
   */
  statusOff?: JSX.Element | string;
  /**
   * When changed, will display a visual indicator to the user that we saved their change.
   */
  autoSave?: boolean;
  /**
   * Label to show when autoSave is true, and we need to notify the user that we saved their change.
   */
  savedLabel?: string;
  /** Whether the toggle is checked or not. */
  checked?: boolean;
  /**
   * Whether the toggle is disabled or not.
   */
  disabled?: boolean;
  /**
   * Function to call when the toggle is changed. Note that this function should return a promise that resolves to a boolean in order to show the saved.
   */
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => Promise<any> | void;
  /**
   * Function to call when the toggle is changed.
   */
  onToggleChange?: (value: boolean) => void;
}

export function Toggle({
  id = "",
  label = "",
  onChange,
  onToggleChange,
  autoSave = false,
  disabled = false,
  checked = false,
  savedLabel = "Saved",
  statusOn = undefined,
  statusOff = undefined,
  "data-testid": dataTestId,
  inert = false,
}: ToggleProps) {
  const isMounted = useIsMounted();
  const [saved, setSaved] = React.useState(false);

  const handleKeypress = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const input = event.currentTarget;
      if (event.key === "Enter" || event.key === "Space") {
        input.checked = !input.checked;
        const simulatedChangeEvent = {
          ...event,
          target: input,
        };
        handleChange(simulatedChangeEvent);
      }
    },
    []
  );

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const onChangeResult = onChange(event);
      // istanbul ignore else - no-op
      if (typeof onChangeResult === "object" && "then" in onChangeResult) {
        onChangeResult.then(() => {
          if (!isMounted() || !autoSave) {
            return;
          }
          setSaved(true);
        });
      }
      if (onToggleChange) {
        onToggleChange(event.target.checked);
      }
    },
    [autoSave, onChange, onToggleChange]
  );

  React.useEffect(() => {
    if (!saved) {
      return;
    }
    const interval = setInterval(setStateValue(setSaved, false), 1000);
    return () => clearInterval(interval);
  }, [saved]);

  const icon = checked ? statusOn : statusOff;

  if (inert === true) {
    return (
      <div
        className={cx(styles.wrapper)}
        data-testid={`${dataTestId}-disabled`}
      >
        <Flex>
          <Icon icon="lock" size={16} variant="filled" color="#949494" />
          <Text size="small" data-testid={`${dataTestId}-icon-text`}>
            {checked ? "ON" : "OFF"}
          </Text>
        </Flex>
      </div>
    );
  }

  return (
    <div
      className={cx(styles.wrapper, {
        [styles.onIcon]: checked,
        [styles.offIcon]: !checked,
        [styles.iconToggle]: icon,
      })}
    >
      <label data-testid={`${dataTestId}-lbl`} htmlFor={id}>
        <span className="screen-reader-only">{label}</span>
        {icon}
        <input
          id={id}
          data-testid={`${dataTestId}-chk`}
          className={styles.toggle}
          type="checkbox"
          tabIndex={0}
          checked={checked}
          disabled={disabled}
          onChange={handleChange}
          onKeyUp={handleKeypress}
        />
      </label>
      {autoSave && saved ? (
        <span className={styles.bubble} data-testid={`${dataTestId}-bubble`}>
          {savedLabel}
        </span>
      ) : null}
    </div>
  );
}
